export type MediaLibraryWorkspace = 'developer' | 'partner' | 'sales' | 'admin';
export type MediaLibraryTargetType = 'org' | 'project' | 'property' | 'phase';
export type MediaLibraryTab = 'view' | 'upload';

export type MediaLibraryPopupOptions = {
    tab?: MediaLibraryTab;
    targetType: MediaLibraryTargetType;
    slotKey: string;
    targetId?: string | null;
    targetLabel?: string | null;
    assetId?: string | null;
    visibility?: '' | 'public' | 'private';
    popup?: boolean;
};

export const MEDIA_LIBRARY_DIALOG_OPEN_EVENT = 'nibni:open-media-library-dialog';
export const MEDIA_LIBRARY_DIALOG_MESSAGE_TYPE = 'nibni:media-library';
export const MEDIA_LIBRARY_POPUP_BLOCKED_EVENT = 'nibni:media-library-popup-blocked';

export type MediaLibraryDialogRequestDetail = {
    workspace: MediaLibraryWorkspace;
    options: MediaLibraryPopupOptions;
    url: string;
};

export type MediaLibraryPopupBlockedDetail = MediaLibraryDialogRequestDetail;

const IMAGE_EXTENSIONS = /\.(png|jpe?g|webp|gif|avif|svg)$/i;

function resolveAppBasePath(pathname: string): string {
    const currentPath = String(pathname || '').trim();
    if (currentPath.includes('/app/')) return '/app/';
    if (currentPath.endsWith('/app')) return '/app/';
    return '/';
}

function serializeQueryParams(params?: URLSearchParams | Record<string, string | null | undefined>): string {
    if (!params) return '';
    if (params instanceof URLSearchParams) return params.toString();

    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        const normalized = String(value || '').trim();
        if (!normalized) continue;
        query.set(key, normalized);
    }
    return query.toString();
}

export function buildWorkspaceRouteUrl(
    workspace: MediaLibraryWorkspace,
    routePath: string,
    params?: URLSearchParams | Record<string, string | null | undefined>
): string {
    const normalizedPath = String(routePath || '').trim().replace(/^\/+/, '');
    const query = serializeQueryParams(params);

    if (typeof window === 'undefined') {
        return `/${workspace}/${normalizedPath}${query ? `?${query}` : ''}`;
    }

    const basePath = resolveAppBasePath(window.location.pathname || '');
    return `${window.location.origin}${basePath}${workspace}/${normalizedPath}${query ? `?${query}` : ''}`;
}

export function buildMediaLibraryPopupUrl(workspace: MediaLibraryWorkspace, options: MediaLibraryPopupOptions): string {
    const tab = options.tab === 'upload' ? 'upload' : 'view';
    const params = new URLSearchParams({
        target_type: options.targetType,
        slot_key: String(options.slotKey || '').trim(),
    });

    const targetId = String(options.targetId || '').trim();
    if (options.targetType !== 'org' && targetId) {
        params.set('target_id', targetId);
    }

    const targetLabel = String(options.targetLabel || '').trim();
    if (options.targetType === 'phase' && targetLabel) {
        params.set('target_label', targetLabel);
    }

    const assetId = String(options.assetId || '').trim();
    if (assetId) params.set('asset_id', assetId);

    const visibility = String(options.visibility || '').trim().toLowerCase();
    if (visibility === 'public' || visibility === 'private') {
        params.set('visibility', visibility);
    }

    if (options.popup !== false) {
        params.set('popup', '1');
    }

    return buildWorkspaceRouteUrl(workspace, `media/${tab}`, params);
}

export function openMediaLibraryPopup(workspace: MediaLibraryWorkspace, options: MediaLibraryPopupOptions): Window | null {
    if (typeof window === 'undefined') return null;

    const url = buildMediaLibraryPopupUrl(workspace, options);
    const openEvent = new CustomEvent<MediaLibraryDialogRequestDetail>(MEDIA_LIBRARY_DIALOG_OPEN_EVENT, {
        detail: {
            workspace,
            options,
            url
        },
        cancelable: true
    });
    window.dispatchEvent(openEvent);
    if (openEvent.defaultPrevented) {
        return null;
    }

    const width = Math.max(1080, Math.min(window.screen.availWidth - 80, 1440));
    const height = Math.max(760, Math.min(window.screen.availHeight - 80, 980));
    const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
    const popup = window.open(
        url,
        `nibni-media-library-${workspace}`,
        [
            'popup=yes',
            'resizable=yes',
            'scrollbars=yes',
            `width=${width}`,
            `height=${height}`,
            `left=${left}`,
            `top=${top}`,
        ].join(',')
    );

    if (!popup) {
        window.dispatchEvent(new CustomEvent<MediaLibraryPopupBlockedDetail>(MEDIA_LIBRARY_POPUP_BLOCKED_EVENT, {
            detail: {
                workspace,
                options,
                url
            }
        }));
        return null;
    }

    popup?.focus();
    return popup;
}

export function isImageLikeFileName(name: string | null | undefined): boolean {
    return IMAGE_EXTENSIONS.test(String(name || '').trim());
}
